<div
  class="inset-0 flex flex-col min-w-0 overflow-hidden bg-card dark:bg-transparent"
>
  <!-- Header -->
  <div
    class="relative flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between py-8 px-6 md:px-8 border-b"
  >
    <div class="absolute inset-x-0 bottom-0" *ngIf="isLoading">
      <mat-progress-bar [mode]="'indeterminate'"></mat-progress-bar>
    </div>

    <div class="text-4xl font-extrabold tracking-tight">User Invitations</div>

    <div class="flex flex-shrink-0 mt-6 sm:mt-0 sm:ml-4 gap-3 items-end">
      <!-- Email search -->
      <mat-form-field
        class="fuse-mat-dense fuse-mat-no-subscript min-w-64"
        matTooltip="Partial, case-insensitive email match"
        floatLabel="always"
      >
        <mat-label>Email</mat-label>
        <mat-icon matPrefix [svgIcon]="'heroicons_outline:search'"></mat-icon>
        <input
          matInput
          [formControl]="searchInputControl"
          [autocomplete]="'off'"
          [placeholder]="'Search by email'"
        />
      </mat-form-field>

      <!-- Role filter -->
      <mat-form-field
        class="fuse-mat-dense fuse-mat-no-subscript min-w-48"
        floatLabel="always"
      >
        <mat-label>Role</mat-label>
        <mat-select
          [(value)]="filterByRole"
          (selectionChange)="filterByRoleChanged($event)"
          placeholder="Select a role"
        >
          <mat-option [value]="null">All roles</mat-option>
          <mat-option *ngFor="let r of roleOptions" [value]="r">
            {{ r | snakeCaseSplit | titlecase }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Invite -->
      <button
        mat-flat-button
        color="primary"
        (click)="invite()"
        [permission]="'user_invitations:write'"
        [removeOnDenied]="true"
      >
        <mat-icon
          class="icon-size-5 mr-2"
          [svgIcon]="'heroicons_outline:mail'"
        ></mat-icon>
        Invite User
      </button>
    </div>
  </div>

  <!-- Main -->
  <div class="flex flex-auto">
    <div class="flex-col w-full sm:mb-18">
      <ng-container *ngIf="total > 0; else noResults">
        <div class="overflow-x-auto sm:overflow-y-auto" cdkScrollable>
          <table
            class="w-full min-w-320 table-fixed bg-transparent"
            [ngClass]="{ 'pointer-events-none': isLoading }"
            mat-table
            [dataSource]="dataSource"
            [trackBy]="trackByFn"
          >
            <ng-container matColumnDef="email">
              <th
                class="bg-gray-50 dark:bg-black dark:bg-opacity-5"
                mat-header-cell
                *matHeaderCellDef
              >
                Email
              </th>
              <td class="pr-8 truncate" mat-cell *matCellDef="let invitation">
                <div class="font-bold">{{ invitation.email }}</div>
                <div class="text-gray-400 text-sm">{{ invitation.id }}</div>
              </td>
            </ng-container>

            <ng-container matColumnDef="role">
              <th
                class="w-48 bg-gray-50 dark:bg-black dark:bg-opacity-5"
                mat-header-cell
                *matHeaderCellDef
              >
                Role
              </th>
              <td class="pr-4" mat-cell *matCellDef="let invitation">
                {{ invitation.role | snakeCaseSplit | titlecase }}
              </td>
            </ng-container>

            <ng-container matColumnDef="created_at">
              <th
                class="w-32 bg-gray-50 dark:bg-black dark:bg-opacity-5"
                mat-header-cell
                *matHeaderCellDef
              >
                Created
              </th>
              <td class="pr-4" mat-cell *matCellDef="let invitation">
                {{ invitation.created_at | timeAgo }}
              </td>
            </ng-container>

            <ng-container matColumnDef="expires_at">
              <th
                class="w-32 bg-gray-50 dark:bg-black dark:bg-opacity-5"
                mat-header-cell
                *matHeaderCellDef
              >
                Expires
              </th>
              <td class="pr-4" mat-cell *matCellDef="let invitation">
                {{ invitation.expires_at | timeAgo }}
              </td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th
                class="w-28 bg-gray-50 dark:bg-black dark:bg-opacity-5"
                mat-header-cell
                *matHeaderCellDef
              >
                Status
              </th>
              <td class="pr-4" mat-cell *matCellDef="let invitation">
                <span
                  *ngIf="isExpired(invitation); else activeStatus"
                  class="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700"
                >
                  Expired
                </span>
                <ng-template #activeStatus>
                  <span
                    class="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700"
                  >
                    Active
                  </span>
                </ng-template>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th
                class="w-20 pr-8 bg-gray-50 dark:bg-black dark:bg-opacity-5"
                mat-header-cell
                *matHeaderCellDef
              ></th>
              <td class="pr-8" mat-cell *matCellDef="let invitation">
                <button
                  class="ml-auto -mr-4"
                  mat-icon-button
                  [matMenuTriggerFor]="actionsMenu"
                >
                  <mat-icon
                    class="icon-size-5"
                    [svgIcon]="'heroicons_solid:dots-vertical'"
                  ></mat-icon>
                </button>

                <mat-menu #actionsMenu="matMenu">
                  <button
                    mat-menu-item
                    (click)="copyUrl(invitation)"
                    [disabled]="!invitation.invitation_url"
                  >
                    <mat-icon
                      class="icon-size-5 mr-3"
                      [svgIcon]="'heroicons_outline:clipboard-copy'"
                    ></mat-icon>
                    <span>Copy invitation link</span>
                  </button>
                  <button
                    mat-menu-item
                    (click)="resend(invitation)"
                    [permission]="'user_invitations:write'"
                  >
                    <mat-icon
                      class="icon-size-5 mr-3"
                      [svgIcon]="'heroicons_outline:mail'"
                    ></mat-icon>
                    <span>Resend</span>
                  </button>
                  <button
                    mat-menu-item
                    (click)="delete(invitation)"
                    [permission]="'user_invitations:write'"
                  >
                    <mat-icon
                      class="icon-size-5 mr-3 text-warn"
                      [svgIcon]="'heroicons_outline:trash'"
                    ></mat-icon>
                    <span class="text-warn">Revoke</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr
              class="shadow"
              mat-header-row
              *matHeaderRowDef="tableColumns; sticky: true"
            ></tr>
            <tr
              class="h-18 hover:bg-gray-100 dark:hover:bg-hover"
              mat-row
              *matRowDef="let invitation; columns: tableColumns"
            ></tr>
          </table>
        </div>
      </ng-container>

      <mat-paginator
        class="sm:inset-x-0 sm:bottom-0 border-b sm:border-t sm:border-b-0 z-10 bg-gray-50 dark:bg-transparent"
        [ngClass]="{ 'pointer-events-none': isLoading }"
        [length]="total"
        [pageSize]="25"
        [pageSizeOptions]="pageSizeOptions"
        [showFirstLastButtons]="true"
        [hidden]="total === 0"
      ></mat-paginator>

      <ng-template #noResults>
        <div
          *ngIf="!isLoading"
          class="p-8 sm:p-16 border-t text-2xl font-semibold tracking-tight text-center"
        >
          No pending invitations for this account
          <div class="text-red-500 text-base" *ngIf="errorMessage">
            {{ errorMessage }}
          </div>
        </div>
        <div
          *ngIf="isLoading"
          class="p-8 sm:p-16 border-t text-2xl font-semibold tracking-tight text-center"
        >
          Loading...
        </div>
      </ng-template>
    </div>
  </div>
</div>
